import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { listMyHouses, type BidderHouse } from '@auctisync/api-client';
import { supabase } from '../lib/supabase';
import { useSession } from './useSession';

/**
 * Bidder multi-house context (shared app, app.auctisync.com).
 *
 * One AuctiSync account can be registered with several auction houses. After
 * sign-in we load the caller's registered houses via the `get_my_registered_tenants`
 * RPC (SECURITY DEFINER, scoped to auth.uid()'s own ACTIVE memberships — the
 * post-auth + own-rows-only privacy guardrails live in the DB, not here) and
 * track which house the bidder is currently "in". BidderHome routes on the
 * count (0 = onboard, 1 = auto-enter, 2+ = pick); PublicAuctionList scopes the
 * catalog to the selected house.
 */
/**
 * The house shape now comes from `@auctisync/api-client`, where the RPC that
 * produces it lives. It was declared here as well until 2026-09-03 — two
 * statements of one contract, and `houses.ts` carried a logged follow-up saying
 * exactly that. Re-exported so existing imports of this module keep working.
 */
export type { BidderHouse };

interface BidderHousesValue {
  houses: BidderHouse[];
  loading: boolean;
  error: string | null;
  /** The house the bidder is currently in — null until chosen (for 2+ houses). */
  selectedId: string | null;
  /** The validated row for selectedId, or null. */
  selected: BidderHouse | null;
  selectHouse: (id: string) => void;
  clearSelection: () => void;
  /**
   * The house a PAGE found out about, from the auction it is showing.
   *
   * 🔴 THIS IS FOR CHROME ONLY — the nav plate, the footer, and the sentences
   * that name the house. It is deliberately NOT `selected`, which scopes the
   * catalogue and decides routing: a discovered house is something the visitor
   * navigated to, not something they are a member of, and letting it act as a
   * membership would put a bidder in a house they cannot bid in.
   *
   * It exists because a signed-out visitor arriving on a lot from a search
   * result has no memberships at all, and a white-label site that greets them
   * with somebody else's brand has failed at its one job.
   */
  discovered: BidderHouse | null;
  /** Publish the house this page is about. Pass null on a page with no house. */
  discoverHouse: (house: BidderHouse | null) => void;
  /** What the CHROME should render: the chosen house, else the discovered one. */
  active: BidderHouse | null;
}

const STORE_KEY = 'auctisync.bidder-house';
const BidderHousesContext = createContext<BidderHousesValue | null>(null);

export function BidderHousesProvider({
  children,
  overrideHouse,
}: {
  children: ReactNode;
  /**
   * Force a single "selected" house and skip the registered-houses RPC. Used by
   * the onboarding preview surface (E1): the owner isn't a bidder-member of
   * their own tenant, but the catalog components scope + brand off the selected
   * house, so the preview injects the tenant here and everything downstream
   * (PublicAuctionList scope, brand tokens) resolves through the owner's own
   * staff-RLS session. Data access is still gated by RLS — this only sets the
   * client-side scope/brand.
   */
  overrideHouse?: BidderHouse | null;
}) {
  // Keyed on WHO is signed in, never the session object (see useSession): this
  // provider wraps every bidder page, and each tab switch re-read the houses
  // and put the shell back into "loading". A house approved mid-visit now shows
  // on the next sign-in or page load (only active registrations are listed).
  const { userId } = useSession();
  const [houses, setHouses] = useState<BidderHouse[]>([]);
  const [discovered, setDiscovered] = useState<BidderHouse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORE_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Preview override: pin to the injected house, don't touch the RPC or
    // localStorage. Re-runs only if the override identity changes.
    if (overrideHouse) {
      setHouses([overrideHouse]);
      setSelectedIdState(overrideHouse.tenant_id);
      setLoading(false);
      setError(null);
      return;
    }
    if (userId === null) {
      setHouses([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      // One implementation of "which houses am I in", shared with the phone.
      const { houses: rows, error: rpcErr } = await listMyHouses(supabase);
      if (cancelled) return;
      if (rpcErr !== undefined) {
        setError(rpcErr);
        setHouses([]);
        setLoading(false);
        return;
      }
      setHouses(rows);
      // Auto-enter the sole house; for multiple keep a still-valid prior pick,
      // else leave unset so BidderHome shows the picker (a first choice is forced).
      setSelectedIdState((prev) => {
        if (rows.length === 1) return rows[0]?.tenant_id ?? null;
        if (prev && rows.some((h) => h.tenant_id === prev)) return prev;
        return null;
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, overrideHouse]);

  // Persist the current house across reloads. Never in preview-override mode —
  // the injected tenant must not clobber the bidder's real house selection.
  useEffect(() => {
    if (overrideHouse) return;
    try {
      if (selectedId) localStorage.setItem(STORE_KEY, selectedId);
      else localStorage.removeItem(STORE_KEY);
    } catch {
      /* private mode — ignore */
    }
  }, [selectedId, overrideHouse]);

  const selectHouse = useCallback(
    (id: string) => {
      setSelectedIdState(houses.some((h) => h.tenant_id === id) ? id : null);
    },
    [houses],
  );

  const clearSelection = useCallback(() => setSelectedIdState(null), []);

  const discoverHouse = useCallback((house: BidderHouse | null) => {
    // Compared by id so a page that re-renders with an equal-but-new object
    // does not restart the render loop it is inside.
    setDiscovered((prev) => (prev?.tenant_id === house?.tenant_id ? prev : house));
  }, []);

  const value = useMemo<BidderHousesValue>(() => {
    const selected = houses.find((h) => h.tenant_id === selectedId) ?? null;
    return {
      houses,
      loading,
      error,
      // Only expose a selection that's still a valid membership.
      selectedId: selected ? selectedId : null,
      selected,
      selectHouse,
      clearSelection,
      discovered,
      discoverHouse,
      active: selected ?? discovered,
    };
  }, [houses, loading, error, selectedId, selectHouse, clearSelection, discovered, discoverHouse]);

  return <BidderHousesContext.Provider value={value}>{children}</BidderHousesContext.Provider>;
}

export function useBidderHouses(): BidderHousesValue {
  const ctx = useContext(BidderHousesContext);
  if (!ctx) {
    throw new Error('useBidderHouses must be used within a BidderHousesProvider');
  }
  return ctx;
}
